import { DealStage, DealStagesResponse } from "@/pages/deals/types";
import {
  ClosedLostReason,
  ClosedLostReasonsResponse
} from "@/entities/settings/types";
import useAPI, { useAPIMutation, useResetAPI } from "@/services/api/hook";
import { ExtendedUseQueryResult } from "@/services/api/types";

export const useClosedLostReasons =
  (): ExtendedUseQueryResult<ClosedLostReasonsResponse> =>
    useAPI("settings/deals/closedLostReasons");

export const useCreateClosedLostReason = () => {
  return useAPIMutation<
    {
      data: {
        name: string;
      };
    },
    ClosedLostReason
  >("settings/deals/closedLostReasons/create", {
    method: "POST"
  });
};

export const useUpdateClosedLostReason = () => {
  return useAPIMutation<
    {
      pathParams: {
        closedLostReasonId: string;
      };
      data: {
        name: string;
      };
    },
    ClosedLostReason
  >("settings/deals/closedLostReasons/update", {
    method: "PUT"
  });
};

export const useDeleteClosedLostReason = () => {
  return useAPIMutation<
    {
      pathParams: {
        closedLostReasonId: string;
      };
    },
    void
  >("settings/deals/closedLostReasons/delete", {
    method: "DELETE"
  });
};

export const useUpdateClosedLostReasonsOrder = () => {
  return useAPIMutation<
    {
      data: number[];
    },
    void
  >("settings/deals/closedLostReasons/order", {
    method: "PUT"
  });
};

export const useUpdateDealStagesOrder = () => {
  return useAPIMutation<
    {
      data: number[];
    },
    void
  >("settings/deals/stages/order", {
    method: "PUT"
  });
};

export const useDealStages = (
  includeAdditionalInfo?: boolean
): ExtendedUseQueryResult<DealStagesResponse> =>
  useAPI("settings/deals/stages", {
    queryParams: includeAdditionalInfo
      ? { include_additional_info: "true" }
      : undefined
  });

export const useCreateDealStage = () => {
  return useAPIMutation<
    {
      data: {
        name: string;
        milestone: number;
      };
    },
    { id: number }
  >("settings/deals/stages", {
    method: "POST"
  });
};

export const useDeleteDealStage = () => {
  return useAPIMutation<
    {
      pathParams: {
        stageId: string;
      };
    },
    void
  >("settings/deals/stages/stageById", {
    method: "DELETE"
  });
};

export const useUpdateDealStage = () => {
  return useAPIMutation<
    {
      pathParams: {
        stageId: string;
      };
      data: {
        name: string;
        milestone: number;
        positionInPipeline?: string;
      };
    },
    DealStage
  >("settings/deals/stages/stageById", {
    method: "PUT"
  });
};

export const useStageById = (
  id: string,
  enabled = true
): ExtendedUseQueryResult<DealStage> =>
  useAPI(
    "settings/deals/stages/stageById",
    {
      pathParams: { stageId: id },
      queryParams: { include_additional_info: "true" }
    },
    { enabled }
  );

export const useResetStageById = () => {
  return useResetAPI("settings/deals/stages/stageById");
};
